import React, { useEffect, useMemo, useState } from "react";
import DRLogo from "../MyImages/DRLogo.png";
import MyQr from "../MyImages/MyQr.png";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";

import { db } from "../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";

export default function TourForm() {
  const navigate = useNavigate();

  const PAYPAL_EMAIL = "Simanofusa@gmail.com";
  const FEE_USD = 75;
  const MAX_ACOMP = 3;

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successId, setSuccessId] = useState("");

  // ✅ number of companions (0..3)
  const [companionsCount, setCompanionsCount] = useState(0);

  // ✅ dynamic companions array
  const [companions, setCompanions] = useState([]);

  // main student fields
  const [formData, setFormData] = useState({
    nombreEstudiante: "",
    edad: "",
    pais: "",
    ciudad: "",
    email: "",
    telefono: "",
    instrumento: "",
    institucion: "",
    tallaCamisa: "",

    // audition link
    videoLink: "",

    // payment
    paymentConfirmed: false,
    paypalTransactionId: "",
  });

  useEffect(() => {
    try {
      console.log("🔥 Firestore projectId:", db?.app?.options?.projectId);
      console.log("🔥 Firestore appId:", db?.app?.options?.appId);
    } catch (e) {
      console.warn("No se pudo leer db.app.options:", e);
    }
  }, []);

  // Keep companions array length in sync with companionsCount
  useEffect(() => {
    setCompanions((prev) => {
      const next = [...prev];

      // add missing
      while (next.length < companionsCount) {
        next.push({ nombre: "", edad: "", relacion: "" });
      }

      // remove extras
      while (next.length > companionsCount) {
        next.pop();
      }

      return next;
    });
  }, [companionsCount]);

  const fadeUp = {
    hidden: { opacity: 0, y: 16, filter: "blur(10px)" },
    show: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.9, ease: "easeOut" },
    },
  };

  const softIn = {
    hidden: { opacity: 0, y: 10, scale: 0.99, filter: "blur(12px)" },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      filter: "blur(0px)",
      transition: { duration: 0.85, ease: "easeOut" },
    },
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setFormData((p) => ({ ...p, [name]: val }));
  };

  const handleCompanionChange = (index, field, value) => {
    setCompanions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const isValidUrl = (url) => {
    if (!url) return false;
    try {
      const u = new URL(url);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  };

  const companionsCleaned = useMemo(() => {
    return companions.map((c) => ({
      nombre: (c.nombre || "").trim(),
      edad: c.edad ? Number(c.edad) : null,
      relacion: (c.relacion || "").trim(),
    }));
  }, [companions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessId("");

    console.log("🚀 [SUBMIT] clicked");
    console.log("🧾 [SUBMIT] formData:", formData);
    console.log("🧾 [SUBMIT] companionsCount:", companionsCount);
    console.log("🧾 [SUBMIT] companions:", companions);

    // Required basics
    if (!formData.nombreEstudiante?.trim() || !formData.email?.trim() || !formData.instrumento?.trim()) {
      setErrorMsg("Por favor completa: Nombre del estudiante, Email e Instrumento.");
      return;
    }

    if (!formData.tallaCamisa) {
      setErrorMsg("Por favor selecciona una talla de camisa.");
      return;
    }

    // Audition link required
    if (!formData.videoLink?.trim()) {
      setErrorMsg("Por favor pega el link de tu video (2 escalas + etude o concierto).");
      return;
    }
    if (!isValidUrl(formData.videoLink.trim())) {
      setErrorMsg("El link del video no parece válido. Asegúrate que empiece con http:// o https://");
      return;
    }

    // Payment confirmation required
    if (!formData.paymentConfirmed) {
      setErrorMsg(`Para enviar la solicitud, confirma que pagarás la cuota de $${FEE_USD} por PayPal.`);
      return;
    }

    // Companions validation (if any)
    if (companionsCount > 0) {
      for (let i = 0; i < companionsCleaned.length; i++) {
        const c = companionsCleaned[i];
        if (!c.nombre || !c.relacion) {
          setErrorMsg(`Acompañante #${i + 1}: completa nombre y relación.`);
          return;
        }
      }
    }

    const docToSave = {
      estudiante: {
        nombre: formData.nombreEstudiante.trim(),
        edad: formData.edad ? Number(formData.edad) : null,
        pais: formData.pais.trim(),
        ciudad: formData.ciudad.trim(),
        email: formData.email.trim().toLowerCase(),
        telefono: formData.telefono.trim(),
        instrumento: formData.instrumento.trim(),
        institucion: formData.institucion.trim(),
        tallaCamisa: formData.tallaCamisa,
      },

      audition: {
        videoLink: formData.videoLink.trim(),
        requirements: "2 scales + etude or current concert piece",
      },

      payment: {
        feeUSD: FEE_USD,
        method: "PayPal",
        paypalTo: PAYPAL_EMAIL,
        userConfirmed: Boolean(formData.paymentConfirmed),
        paypalTransactionId: formData.paypalTransactionId.trim() || null,
      },

      companionsCount,
      acompanantes: companionsCount > 0 ? companionsCleaned : [],

      origen: "dr-tour-form",
      createdAt: serverTimestamp(),
      meta: {
        timeLocal: new Date().toString(),
        browser: navigator.userAgent,
      },
    };

    try {
      setLoading(true);

      console.log("📨 writing to Firestore: drTourApplications");
      console.log("📨 payload:", docToSave);

      const ref = await addDoc(collection(db, "drTourApplications"), docToSave);

      console.log("✅ saved doc id:", ref.id);
      setSuccessId(ref.id);

      setTimeout(() => {
        navigate("/confirmation");
      }, 250);
    } catch (err) {
      console.error("❌ Firestore error:", err);
      setErrorMsg("❌ No se pudo enviar. Error: " + (err?.message || "desconocido"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-white text-slate-900 overflow-hidden">
      {/* Fondo */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-[#EAF6FF] via-[#FFF3EC] to-[#F3FFF7]" />
        <div className="absolute -top-28 -left-32 h-[520px] w-[520px] rounded-full bg-blue-500/18 blur-3xl" />
        <div className="absolute -bottom-28 -right-32 h-[560px] w-[560px] rounded-full bg-red-500/14 blur-3xl" />
        <div className="absolute top-[35%] left-[55%] h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-amber-300/22 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.08] bg-[repeating-linear-gradient(135deg,_rgba(15,23,42,0.45)_0px,_rgba(15,23,42,0.45)_1px,_transparent_1px,_transparent_16px)]" />
      </div>

      <div className="relative z-10">
        {/* Barra superior */}
        <nav className="w-full">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-6 md:px-10 py-5">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="text-sm md:text-base font-semibold tracking-[0.22em]"
            >
              SIMANOF FESTIVAL
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.08 }}
              className="flex items-center gap-5 text-xs md:text-sm tracking-wide"
            >
              <Link to="/" className="text-slate-700 hover:text-slate-900 transition">
                Volver
              </Link>
            </motion.div>
          </div>

          <div className="absolute inset-x-0 top-0 h-[74px] -z-10 bg-white/70 backdrop-blur-md border-b border-white/70" />
        </nav>

        {/* Contenido */}
        <div className="mx-auto max-w-5xl px-6 md:px-10 pt-10 pb-16">
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="flex flex-col items-center text-center">
            <img
              src={DRLogo}
              alt="Logo Festival República Dominicana"
              className="h-28 md:h-36 lg:h-44 w-auto drop-shadow-[0_22px_70px_rgba(0,0,0,0.12)]"
            />

            <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-white/70 bg-white/75 px-4 py-2 backdrop-blur-md shadow-sm">
              <span className="h-2 w-2 rounded-full bg-blue-600" />
              <span className="text-xs tracking-[0.30em] text-slate-700">SOLICITUD • PUNTA CANA</span>
            </div>

            <h1 className="mt-6 text-3xl md:text-5xl font-light tracking-wide">Formulario de Aplicación</h1>

            <p className="mt-4 text-slate-700 text-sm md:text-base max-w-2xl leading-relaxed">
              Completa tu información para aplicar al festival. Esto nos ayuda a organizar instrumentos, logística,
              playeras y acompañantes.
            </p>

            <div className="mt-8 h-px w-full bg-gradient-to-r from-transparent via-blue-600/20 to-transparent" />
            <div className="-mt-px h-px w-full bg-gradient-to-r from-transparent via-red-600/15 to-transparent" />
          </motion.div>

          <motion.div
            variants={softIn}
            initial="hidden"
            animate="show"
            className="mt-10 rounded-[34px] border border-white/70 bg-white/75 backdrop-blur-md shadow-[0_22px_80px_rgba(2,6,23,0.08)] overflow-hidden"
          >
            <form onSubmit={handleSubmit} className="p-7 md:p-10">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white/70 px-4 py-2 backdrop-blur-md shadow-sm">
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                  <span className="text-xs tracking-[0.34em] text-slate-700">INFORMACIÓN DEL ESTUDIANTE</span>
                </div>
                <p className="text-xs tracking-[0.30em] text-slate-500">COMPLETA TODO CON PRECISIÓN</p>
              </div>

              {errorMsg && (
                <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4">
                  <p className="text-sm text-red-700">{errorMsg}</p>
                </div>
              )}

              {successId && (
                <div className="mt-6 rounded-2xl border border-green-200 bg-green-50 px-5 py-4">
                  <p className="text-sm text-green-800">
                    ✅ Guardado en Firestore. ID: <span className="font-mono">{successId}</span>
                  </p>
                </div>
              )}

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="Nombre y Apellido del Estudiante" name="nombreEstudiante" placeholder="Ej: María Pérez" value={formData.nombreEstudiante} onChange={handleChange} />
                <Input label="Edad" name="edad" type="number" placeholder="Ej: 16" value={formData.edad} onChange={handleChange} />

                <Input label="País" name="pais" placeholder="Ej: Estados Unidos" value={formData.pais} onChange={handleChange} />
                <Input label="Ciudad" name="ciudad" placeholder="Ej: Houston" value={formData.ciudad} onChange={handleChange} />

                <Input label="Correo Electrónico" name="email" type="email" placeholder="Ej: maria@email.com" value={formData.email} onChange={handleChange} />
                <Input label="Teléfono" name="telefono" placeholder="Ej: +1 (555) 123-4567" value={formData.telefono} onChange={handleChange} />

                <Input label="Instrumento" name="instrumento" placeholder="Ej: Violín" value={formData.instrumento} onChange={handleChange} />
                <Input label="Escuela o Institución Musical" name="institucion" placeholder="Ej: Conservatorio / Escuela" value={formData.institucion} onChange={handleChange} />

                {/* Talla camisa */}
                <div className="md:col-span-2">
                  <label className="block text-xs tracking-[0.30em] text-slate-600">TALLA DE CAMISA</label>
                  <select
                    name="tallaCamisa"
                    value={formData.tallaCamisa}
                    onChange={handleChange}
                    className="mt-3 w-full rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-md px-5 py-4 text-slate-800 outline-none transition focus:border-slate-300 focus:ring-2 focus:ring-blue-600/10"
                  >
                    <option value="" disabled>Selecciona...</option>
                    <option value="YS">Juvenil Pequeña</option>
                    <option value="YM">Juvenil Mediana</option>
                    <option value="YL">Juvenil Grande</option>
                    <option value="S">Pequeña (S)</option>
                    <option value="M">Mediana (M)</option>
                    <option value="L">Grande (L)</option>
                    <option value="XL">Extra Grande (XL)</option>
                    <option value="XXL">Doble Extra Grande (XXL)</option>
                  </select>
                </div>

                {/* Video link */}
                <div className="md:col-span-2">
                  <div className="rounded-2xl border border-slate-200 bg-white/70 backdrop-blur-md px-5 py-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                      <p className="text-sm tracking-wide text-slate-800">Link de Video (Requisito)</p>
                    </div>

                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                      Proporciona un <b>solo link</b> que muestre:
                      <br />• <b>2 escalas</b>
                      <br />• <b>1 etude</b> o <b>1 concierto/pieza</b> que estás trabajando actualmente
                    </p>

                    <div className="mt-4">
                      <label className="block text-xs tracking-[0.30em] text-slate-600">LINK DEL VIDEO</label>
                      <input
                        name="videoLink"
                        type="url"
                        placeholder="https://youtube.com/... o https://drive.google.com/..."
                        value={formData.videoLink}
                        onChange={handleChange}
                        className="mt-3 w-full rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-md px-5 py-4
                                   text-slate-800 outline-none transition
                                   focus:border-slate-300 focus:ring-2 focus:ring-blue-600/10"
                      />
                      <p className="mt-2 text-xs text-slate-500">
                        Asegúrate que el link tenga permisos de “cualquiera con el link puede ver”.
                      </p>
                    </div>

                    <div className="mt-5 h-[10px] w-full rounded-full bg-gradient-to-r from-blue-600/60 via-sky-300/40 to-transparent" />
                  </div>
                </div>

                {/* PayPal fee + QR */}
                <div className="md:col-span-2">
                  <div className="rounded-2xl border border-slate-200 bg-white/70 backdrop-blur-md px-5 py-5 shadow-sm">
                    <div className="flex items-center gap-3">
                      <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                      <p className="text-sm tracking-wide text-slate-800">Cuota de Aplicación (PayPal)</p>
                    </div>

                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                      Para completar tu solicitud, debes pagar una cuota de <b>${FEE_USD} USD</b> por PayPal a:
                      <br />
                      <b className="text-slate-900">{PAYPAL_EMAIL}</b>
                    </p>

                    <div className="mt-5 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                      <div className="md:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 flex flex-col items-center">
                          <p className="text-xs tracking-[0.30em] text-slate-600">ESCANEA PARA PAGAR</p>
                          <img src={MyQr} alt="PayPal QR" className="mt-3 w-full max-w-[260px] rounded-xl" />
                          <p className="mt-3 text-xs text-slate-500 text-center">
                            Envía <b>${FEE_USD}</b> a <b>{PAYPAL_EMAIL}</b>.
                          </p>
                        </div>
                      </div>

                      <div className="md:col-span-7">
                        <div className="flex flex-wrap items-center gap-3">
                          <a
                            href={`https://www.paypal.com/send?email=${encodeURIComponent(PAYPAL_EMAIL)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center rounded-md px-5 py-3
                                       border border-slate-300 bg-white/80 hover:bg-white hover:border-slate-400 transition
                                       tracking-[0.18em] text-[11px] text-slate-900"
                          >
                            ENVIAR ${FEE_USD} (PAYPAL)
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard?.writeText(PAYPAL_EMAIL);
                              alert("✅ Copiado: " + PAYPAL_EMAIL);
                            }}
                            className="inline-flex items-center justify-center rounded-md px-5 py-3
                                       border border-slate-300 bg-white/80 hover:bg-white hover:border-slate-400 transition
                                       tracking-[0.18em] text-[11px] text-slate-900"
                          >
                            COPIAR EMAIL
                          </button>
                        </div>

                        <div className="mt-5 flex items-start gap-3">
                          <input
                            type="checkbox"
                            name="paymentConfirmed"
                            checked={formData.paymentConfirmed}
                            onChange={handleChange}
                            className="mt-1 h-5 w-5 accent-slate-900"
                          />
                          <p className="text-sm text-slate-700 leading-relaxed">
                            Confirmo que pagaré la cuota de <b>${FEE_USD} USD</b> por PayPal a <b>{PAYPAL_EMAIL}</b>.
                          </p>
                        </div>

                        <div className="mt-5">
                          <Input
                            label="PayPal Transaction ID (opcional pero recomendado)"
                            name="paypalTransactionId"
                            placeholder="Ej: 9AB12345CD678901E"
                            value={formData.paypalTransactionId}
                            onChange={handleChange}
                          />
                          <p className="mt-2 text-xs text-slate-500">
                            Si lo pones, nos ayuda a verificar más rápido.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 h-[10px] w-full rounded-full bg-gradient-to-r from-amber-500/70 via-yellow-200/40 to-transparent" />
                  </div>
                </div>

                {/* Companions selector */}
                <div className="md:col-span-2">
                  <div className="rounded-2xl border border-slate-200 bg-white/70 backdrop-blur-md px-5 py-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <span className="h-2.5 w-2.5 rounded-full bg-red-600" />
                      <p className="text-sm tracking-wide text-slate-800">Acompañantes</p>
                    </div>

                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                      Selecciona cuántas personas vienen contigo (máximo {MAX_ACOMP}).
                    </p>

                    <div className="mt-4">
                      <label className="block text-xs tracking-[0.30em] text-slate-600">NÚMERO DE ACOMPAÑANTES</label>
                      <select
                        value={companionsCount}
                        onChange={(e) => setCompanionsCount(Number(e.target.value))}
                        className="mt-3 w-full rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-md px-5 py-4
                                   text-slate-800 outline-none transition
                                   focus:border-slate-300 focus:ring-2 focus:ring-blue-600/10"
                      >
                        <option value={0}>0</option>
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                      </select>
                    </div>

                    <div className="mt-5 h-[10px] w-full rounded-full bg-gradient-to-r from-red-600/70 via-rose-300/50 to-transparent" />
                  </div>
                </div>

                {/* Companion blocks */}
                {companionsCount > 0 && (
                  <motion.div
                    variants={softIn}
                    initial="hidden"
                    animate="show"
                    className="md:col-span-2 rounded-[30px] border border-white/70 bg-white/80 backdrop-blur-md shadow-[0_18px_55px_rgba(2,6,23,0.06)] overflow-hidden"
                  >
                    <div className="p-6 md:p-7">
                      <div className="inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white/70 px-4 py-2 backdrop-blur-md shadow-sm">
                        <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                        <span className="text-xs tracking-[0.34em] text-slate-700">
                          INFORMACIÓN DE ACOMPAÑANTES
                        </span>
                      </div>

                      <div className="mt-6 grid grid-cols-1 gap-8">
                        {companions.map((c, idx) => (
                          <div key={idx} className="rounded-2xl border border-slate-200 bg-white/70 p-5">
                            <p className="text-xs tracking-[0.30em] text-slate-600">
                              ACOMPAÑANTE #{idx + 1}
                            </p>

                            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
                              <div>
                                <label className="block text-xs tracking-[0.30em] text-slate-600">NOMBRE COMPLETO</label>
                                <input
                                  value={c.nombre}
                                  onChange={(e) => handleCompanionChange(idx, "nombre", e.target.value)}
                                  placeholder="Ej: Juan Pérez"
                                  className="mt-3 w-full rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-md px-5 py-4
                                             text-slate-800 outline-none transition
                                             focus:border-slate-300 focus:ring-2 focus:ring-blue-600/10"
                                />
                              </div>

                              <div>
                                <label className="block text-xs tracking-[0.30em] text-slate-600">EDAD</label>
                                <input
                                  type="number"
                                  value={c.edad}
                                  onChange={(e) => handleCompanionChange(idx, "edad", e.target.value)}
                                  placeholder="Ej: 42"
                                  className="mt-3 w-full rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-md px-5 py-4
                                             text-slate-800 outline-none transition
                                             focus:border-slate-300 focus:ring-2 focus:ring-blue-600/10"
                                />
                              </div>

                              <div>
                                <label className="block text-xs tracking-[0.30em] text-slate-600">RELACIÓN</label>
                                <input
                                  value={c.relacion}
                                  onChange={(e) => handleCompanionChange(idx, "relacion", e.target.value)}
                                  placeholder="Ej: Padre / Madre / Tutor"
                                  className="mt-3 w-full rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-md px-5 py-4
                                             text-slate-800 outline-none transition
                                             focus:border-slate-300 focus:ring-2 focus:ring-blue-600/10"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="h-[5px] w-full bg-gradient-to-r from-blue-600 via-amber-400 to-red-600" />
                  </motion.div>
                )}
              </div>

              {/* Submit */}
              <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
                <button
                  type="submit"
                  disabled={loading}
                  className={`inline-flex items-center justify-center rounded-md px-10 py-3.5
                    border border-slate-300 bg-white/70 transition duration-300 tracking-[0.22em] text-xs md:text-sm text-slate-900
                    ${loading ? "opacity-60 cursor-not-allowed" : "hover:bg-white hover:border-slate-400 hover:scale-[1.04]"}`}
                >
                  {loading ? "ENVIANDO..." : "ENVIAR SOLICITUD"}
                </button>

                <p className="text-xs tracking-[0.26em] text-slate-500">
                  AL ENVIAR, CONFIRMAS QUE LA INFORMACIÓN ES CORRECTA
                </p>
              </div>
            </form>

            <div className="h-[5px] w-full bg-gradient-to-r from-blue-600 via-amber-400 to-red-600" />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Input({ label, name, type = "text", placeholder, value, onChange }) {
  return (
    <div>
      <label className="block text-xs tracking-[0.30em] text-slate-600">{label.toUpperCase()}</label>
      <input
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="mt-3 w-full rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-md px-5 py-4
                   text-slate-800 outline-none transition
                   focus:border-slate-300 focus:ring-2 focus:ring-blue-600/10"
      />
    </div>
  );
}